import appShellStore from '@/lib/offline/appShellStore';

const MANIFEST_CACHE_VERSION = 1;
const MANIFEST_TTL_MINUTES = 60 * 24 * 30;
const RESOLVED_CONFIG_TTL_MINUTES = 60 * 24 * 7;
const RAW_CONFIG_BUNDLE_TTL_MINUTES = 60 * 24 * 7;
const USER_FEATURES_TTL_MINUTES = 60 * 24 * 3;

const SOLUTION_MANIFEST_PREFIX = `smart_task_solution_manifest:v${MANIFEST_CACHE_VERSION}:user:`;
const ACTIVE_SOLUTION_PREFIX = `smart_task_active_solution:v${MANIFEST_CACHE_VERSION}:user:`;
const RESOLVED_CONFIG_PREFIX = `smart_task_resolved_config:v${MANIFEST_CACHE_VERSION}:user:`;
const RAW_CONFIG_BUNDLE_PREFIX = `smart_task_raw_config_bundle:v${MANIFEST_CACHE_VERSION}:user:`;
const USER_FEATURES_PREFIX = `smart_task_user_features:v${MANIFEST_CACHE_VERSION}:user:`;

export interface SmartTaskOfflineSolutionTeam {
  teamId: string;
  solutionKey: string;
  solutionName: string;
  icon: string;
  color: string;
  role: string;
  featureOverrides: Record<string, unknown> | null;
  featurePolicies: Record<string, unknown> | null;
  featurePoliciesByRoleScope?: Record<string, Record<string, unknown>> | null;
  settings: Record<string, unknown> | null;
  itemTypes: string[];
  dashboardKey: string | null;
  memberCount: number;
}

export interface SmartTaskSolutionManifest {
  version: number;
  userId: number;
  clientId: number;
  teams: SmartTaskOfflineSolutionTeam[];
  activeSolutionKey: string | null;
  cachedAt: string;
}

export interface SmartTaskResolvedConfigSnapshot {
  version: number;
  userId: number;
  clientId: number;
  solutionKey: string;
  cachedAt: string;
  resolvedConfig: Record<string, unknown>;
}

export interface SmartTaskRawConfigBundleSnapshot {
  version: number;
  userId: number;
  clientId: number;
  solutionKey: string;
  cachedAt: string;
  rawConfigBundle: Record<string, unknown>;
}

export interface SmartTaskUserFeaturesSnapshot {
  version: number;
  userId: number;
  clientId: number;
  cachedAt: string;
  userFeatures: Record<string, unknown>;
}

function normalizeSolutionKey(solutionKey?: string | null): string {
  return typeof solutionKey === 'string' && solutionKey.trim().length > 0
    ? solutionKey.trim()
    : '__default__';
}

function getManifestKey(userId: number): string {
  return `${SOLUTION_MANIFEST_PREFIX}${userId}`;
}

function getActiveSolutionKey(userId: number): string {
  return `${ACTIVE_SOLUTION_PREFIX}${userId}`;
}

function getResolvedConfigKey(userId: number, solutionKey: string): string {
  return `${RESOLVED_CONFIG_PREFIX}${userId}:solution:${normalizeSolutionKey(solutionKey)}`;
}

function getRawConfigBundleKey(userId: number, solutionKey: string): string {
  return `${RAW_CONFIG_BUNDLE_PREFIX}${userId}:solution:${normalizeSolutionKey(solutionKey)}`;
}

function getUserFeaturesKey(userId: number): string {
  return `${USER_FEATURES_PREFIX}${userId}`;
}

function hasValidIdentity(clientId: number, userId: number): boolean {
  return Number.isFinite(clientId) && clientId > 0 && Number.isFinite(userId) && userId > 0;
}

export async function saveSmartTaskSolutionManifest(params: {
  clientId: number;
  userId: number;
  teams: SmartTaskOfflineSolutionTeam[];
  activeSolutionKey?: string | null;
}): Promise<void> {
  const { clientId, userId, teams, activeSolutionKey = null } = params;
  if (!hasValidIdentity(clientId, userId)) return;

  const manifest: SmartTaskSolutionManifest = {
    version: MANIFEST_CACHE_VERSION,
    userId,
    clientId,
    teams,
    activeSolutionKey,
    cachedAt: new Date().toISOString(),
  };

  await appShellStore.upsertJson(clientId, getManifestKey(userId), manifest as unknown as Record<string, unknown>);

  if (activeSolutionKey) {
    await savePreferredSmartTaskSolution(clientId, userId, activeSolutionKey);
  }
}

export async function getSmartTaskSolutionManifest(
  clientId: number,
  userId: number,
): Promise<SmartTaskSolutionManifest | null> {
  if (!hasValidIdentity(clientId, userId)) return null;

  try {
    const manifest = await appShellStore.getJson<SmartTaskSolutionManifest & Record<string, unknown>>(
      clientId,
      getManifestKey(userId),
    );
    if (!manifest || !Array.isArray(manifest.teams)) return null;
    return manifest;
  } catch (error) {
    console.warn('[SmartTaskOfflineCache] Failed to read solution manifest:', error);
    return null;
  }
}

export async function savePreferredSmartTaskSolution(
  clientId: number,
  userId: number,
  solutionKey: string,
): Promise<void> {
  if (!hasValidIdentity(clientId, userId) || typeof solutionKey !== 'string' || solutionKey.trim().length === 0) {
    return;
  }

  await appShellStore.upsertJson(clientId, getActiveSolutionKey(userId), {
    solutionKey: solutionKey.trim(),
    cachedAt: new Date().toISOString(),
  });
}

export async function getPreferredSmartTaskSolution(
  clientId: number,
  userId: number,
): Promise<string | null> {
  if (!hasValidIdentity(clientId, userId)) return null;

  try {
    const cached = await appShellStore.getJson<{ solutionKey?: string } & Record<string, unknown>>(
      clientId,
      getActiveSolutionKey(userId),
    );
    if (typeof cached?.solutionKey === 'string' && cached.solutionKey.trim().length > 0) {
      return cached.solutionKey.trim();
    }
    return null;
  } catch (error) {
    console.warn('[SmartTaskOfflineCache] Failed to read preferred solution:', error);
    return null;
  }
}

export async function saveSmartTaskResolvedConfigSnapshot(params: {
  clientId: number;
  userId: number;
  solutionKey: string;
  resolvedConfig: Record<string, unknown>;
}): Promise<void> {
  const { clientId, userId, solutionKey, resolvedConfig } = params;
  if (!hasValidIdentity(clientId, userId) || typeof solutionKey !== 'string' || solutionKey.trim().length === 0) {
    return;
  }

  const snapshot: SmartTaskResolvedConfigSnapshot = {
    version: MANIFEST_CACHE_VERSION,
    userId,
    clientId,
    solutionKey: solutionKey.trim(),
    cachedAt: new Date().toISOString(),
    resolvedConfig,
  };

  await appShellStore.upsertJson(
    clientId,
    getResolvedConfigKey(userId, solutionKey.trim()),
    snapshot as unknown as Record<string, unknown>,
  );
}

export async function getSmartTaskResolvedConfigSnapshot(
  clientId: number,
  userId: number,
  solutionKey: string,
): Promise<SmartTaskResolvedConfigSnapshot | null> {
  if (!hasValidIdentity(clientId, userId) || typeof solutionKey !== 'string' || solutionKey.trim().length === 0) {
    return null;
  }

  try {
    const snapshot = await appShellStore.getJson<
      SmartTaskResolvedConfigSnapshot & Record<string, unknown>
    >(clientId, getResolvedConfigKey(userId, solutionKey.trim()));

    if (!snapshot || !snapshot.resolvedConfig || typeof snapshot.resolvedConfig !== 'object') {
      return null;
    }

    return snapshot;
  } catch (error) {
    console.warn('[SmartTaskOfflineCache] Failed to read resolved config snapshot:', error);
    return null;
  }
}

export async function saveSmartTaskRawConfigBundleSnapshot(params: {
  clientId: number;
  userId: number;
  solutionKey?: string | null;
  rawConfigBundle: Record<string, unknown>;
}): Promise<void> {
  const { clientId, userId, solutionKey = null, rawConfigBundle } = params;
  if (!hasValidIdentity(clientId, userId) || !rawConfigBundle || typeof rawConfigBundle !== 'object') {
    return;
  }

  const normalizedSolutionKey = normalizeSolutionKey(solutionKey);
  const snapshot: SmartTaskRawConfigBundleSnapshot = {
    version: MANIFEST_CACHE_VERSION,
    userId,
    clientId,
    solutionKey: normalizedSolutionKey,
    cachedAt: new Date().toISOString(),
    rawConfigBundle,
  };

  await appShellStore.upsertJson(
    clientId,
    getRawConfigBundleKey(userId, normalizedSolutionKey),
    snapshot as unknown as Record<string, unknown>,
  );
}

export async function getSmartTaskRawConfigBundleSnapshot(
  clientId: number,
  userId: number,
  solutionKey?: string | null,
): Promise<SmartTaskRawConfigBundleSnapshot | null> {
  if (!hasValidIdentity(clientId, userId)) return null;

  try {
    const normalizedSolutionKey = normalizeSolutionKey(solutionKey);
    const snapshot = await appShellStore.getJson<
      SmartTaskRawConfigBundleSnapshot & Record<string, unknown>
    >(clientId, getRawConfigBundleKey(userId, normalizedSolutionKey));

    if (!snapshot || !snapshot.rawConfigBundle || typeof snapshot.rawConfigBundle !== 'object') {
      return null;
    }

    return snapshot;
  } catch (error) {
    console.warn('[SmartTaskOfflineCache] Failed to read raw config bundle snapshot:', error);
    return null;
  }
}

export async function saveSmartTaskUserFeaturesSnapshot(params: {
  clientId: number;
  userId: number;
  userFeatures: Record<string, unknown>;
}): Promise<void> {
  const { clientId, userId, userFeatures } = params;
  if (!hasValidIdentity(clientId, userId) || !userFeatures || typeof userFeatures !== 'object') {
    return;
  }

  const snapshot: SmartTaskUserFeaturesSnapshot = {
    version: MANIFEST_CACHE_VERSION,
    userId,
    clientId,
    cachedAt: new Date().toISOString(),
    userFeatures,
  };

  await appShellStore.upsertJson(clientId, getUserFeaturesKey(userId), snapshot as unknown as Record<string, unknown>);
}

export async function getSmartTaskUserFeaturesSnapshot(
  clientId: number,
  userId: number,
): Promise<SmartTaskUserFeaturesSnapshot | null> {
  if (!hasValidIdentity(clientId, userId)) return null;

  try {
    const snapshot = await appShellStore.getJson<
      SmartTaskUserFeaturesSnapshot & Record<string, unknown>
    >(clientId, getUserFeaturesKey(userId));

    if (!snapshot || !snapshot.userFeatures || typeof snapshot.userFeatures !== 'object') {
      return null;
    }

    return snapshot;
  } catch (error) {
    console.warn('[SmartTaskOfflineCache] Failed to read user-features snapshot:', error);
    return null;
  }
}
